/**
 * Place search via Photon (Komoot's OSM geocoder). No key, CORS-open, and it
 * ranks peaks and villages sensibly, which matters when the thing you are
 * looking for is a launch site rather than a street address.
 */

export interface GeocodeResult {
  name: string;
  detail: string;
  lat: number;
  lon: number;
  /** OSM class, used to pick a sensible default zoom */
  kind: string;
}

const ENDPOINT = 'https://photon.komoot.io/api/';

export async function geocode(
  query: string,
  signal?: AbortSignal,
  near?: { lat: number; lon: number }
): Promise<GeocodeResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const params = new URLSearchParams({ q, limit: '8' });
  if (near) {
    params.set('lat', near.lat.toFixed(4));
    params.set('lon', near.lon.toFixed(4));
  }

  const res = await fetch(`${ENDPOINT}?${params}`, { signal });
  if (!res.ok) throw new Error(`Geocoder returned ${res.status}`);

  const json = (await res.json()) as {
    features?: {
      geometry: { coordinates: [number, number] };
      properties: Record<string, string | undefined>;
    }[];
  };

  return (json.features ?? []).map((f) => {
    const p = f.properties;
    const detail = [p.state, p.country].filter(Boolean).join(', ');
    const context = [p.city, p.county].filter(Boolean).join(', ');
    return {
      name: p.name ?? 'Unnamed',
      detail: [context, detail].filter(Boolean).join(' · '),
      lat: f.geometry.coordinates[1],
      lon: f.geometry.coordinates[0],
      kind: p.osm_value ?? p.osm_key ?? 'place',
    };
  });
}

/** Peaks and passes want a closer look than a city does. */
export function zoomForKind(kind: string): number {
  switch (kind) {
    case 'peak':
    case 'saddle':
    case 'ridge':
    case 'cliff':
      return 13.5;
    case 'village':
    case 'hamlet':
    case 'suburb':
      return 12.5;
    case 'city':
    case 'town':
      return 11.5;
    default:
      return 12.5;
  }
}

/** Parse "46.685, 7.865" so pasted coordinates work in the search box. */
export function parseLatLon(query: string): { lat: number; lon: number } | null {
  const m = query.trim().match(/^(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lon = Number(m[2]);
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}
